/* Sohi — entry popup + returning-customer prompt, THEME BUILD.

   ── Why this file exists at all ───────────────────────────────────────────
   The demo's assets/sohi-popup.js is on the excluded list in BOTH
   .shopifyignore and the [environments.dev] ignore array in
   shopify.theme.toml, and rightly so: it carries a localStorage order counter
   that stands in for customer records the static site does not have, and it
   quotes bundle prices off the hardcoded window.SohiCatalog. Neither belongs
   on a real storefront — on Shopify the order count is `customer.orders_count`
   and the prices are the product's own.

   The catch is that sections/sohi-popup.liquid used to <script src> that very
   file. On the live theme the request 404'd, nothing ever read the JSON
   payload the section emits, and the popup simply never appeared on any page.
   The section was correct; it was pointing at a file that is deliberately not
   uploaded. This file is what it points at now — and it is NOT in either
   ignore list, because it must upload.

   ── What the two files share, and what they don't ─────────────────────────
   SHARED (ported here faithfully, deliberately unchanged in behaviour):
     • the modal shell — buildModal / openModal / closeModal
     • the focus trap, Escape-to-close, scrim-click-to-close
     • the 260ms wait before removal, so the CSS fade-out is actually seen
       (it must stay in step with the .26s transitions on .pop__scrim and
       .pop__panel in assets/sohi.css — change one, change the other)
     • focus restoration to whatever element had it before the popup opened
     • try/catch around every localStorage touch, for Safari private mode
   NOT SHARED (the whole reason for the split):
     • no localStorage order counter — cfg.show is decided server-side
     • no window.SohiCatalog, no bundleFacts(), no price arithmetic
     • no window.SohiPopup.recordOrder() — the demo checkout calls that; a
       real order on Shopify is a real order, and the next page load's
       `customer.orders_count` sees it without our help

   If you fix a bug in the shell here, fix it there too. They are two builds of
   one component, not two components.

   ── The one rule that must not be softened ────────────────────────────────
   `cfg.show` is authoritative. Liquid decided it from `customer.orders_count`,
   which is server-side, correct across devices, and impossible to fake by
   clearing a cache. This file must never second-guess it from localStorage.
   The only thing localStorage is allowed to answer here is "have I already
   shown this browser the popup?" — a display-frequency question, not a
   who-is-this question. See docs/client-requirements.md items 17 and 21. */
(function () {
  "use strict";

  /* ---- config ----------------------------------------------------------
     Everything — copy, delay, and which popup to show — arrives in the JSON
     payload sections/sohi-popup.liquid emits at [data-sohi-popup]. There are
     no defaults in this file on purpose: a default here would be a second
     copy of the wording that silently diverges from the theme editor's, and
     the merchant would have no way to tell which one they were looking at.

     If the payload is missing or unparseable, we do NOTHING. A popup with no
     copy is worse than no popup, and a section that failed to render is a
     Liquid problem to fix in Liquid, not something to paper over here. */
  var cfg = (function readConfig() {
    var el = document.querySelector("[data-sohi-popup]");
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent);
      // A payload that isn't an object is as useless as no payload at all.
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
      return null;
    }
  })();

  if (!cfg) return;

  // Ask once per browser, per popup type. Separate keys so that enabling the
  // loyalty prompt later doesn't get vetoed by an entry popup someone
  // dismissed months ago. Same key names as the demo build, so a visitor who
  // saw the demo isn't re-interrupted on the real store.
  var SEEN_ENTRY_KEY = "sohi:seen-entry";
  var SEEN_LOYALTY_KEY = "sohi:seen-loyalty";

  // Safari private mode throws on localStorage rather than returning null, and
  // an exception here would take the whole popup down. Swallow it: the worst
  // case is a visitor who sees the popup once per session instead of once
  // ever, which is a far smaller failure than a script that dies on load.
  function read(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function write(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  /* ---- copy access -----------------------------------------------------
     Reads one field out of a group in the payload. Blank is treated as
     missing: a merchant who clears a field in the theme editor gets an empty
     string, and rendering an empty <h2> is not what they meant. A missing
     field yields "" and the caller decides whether that's fatal. */
  function conf(group, key) {
    var g = cfg[group];
    if (!g || typeof g !== "object") return "";
    var v = g[key];
    if (v === undefined || v === null || v === "") return "";
    return String(v);
  }

  // The section's checkbox. Absent group or absent flag reads as enabled,
  // matching the schema defaults — only an explicit `false` switches it off.
  function enabled(group) {
    var g = cfg[group];
    if (!g || typeof g !== "object") return false; // no copy = nothing to show
    return g.enabled !== false;
  }

  // Milliseconds, already multiplied up from the section's seconds range.
  // Guard against a payload that lost the field: better a sane 6s than a
  // popup that fires at 0ms, on top of a hero that hasn't painted yet.
  function delayMs() {
    var n = parseInt(cfg.delay, 10);
    return isNaN(n) || n < 0 ? 6000 : n;
  }

  /* All copy is merchant-entered and arrives through Liquid's `json` filter,
     which escapes it for JavaScript but not for HTML. Since we build the
     panel with innerHTML, escape again here — otherwise an apostrophe-heavy
     heading is merely ugly and an angle bracket is an injection. */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /* ---- modal shell -----------------------------------------------------
     Ported verbatim in behaviour from the demo build. One builder for both
     popups. Focus is trapped while open and handed back to whatever held it
     before, so a keyboard user is never dropped behind the scrim. */
  var lastFocused = null;

  function closeModal(el) {
    el.removeAttribute("data-open");
    // Wait out the fade before pulling it from the DOM, so the exit is seen.
    // 260ms matches the .26s transitions in assets/sohi.css.
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 260);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
    lastFocused = null;
  }

  function openModal(el) {
    lastFocused = document.activeElement;
    document.body.appendChild(el);
    // Force a reflow so the [data-open] transition actually runs rather than
    // the element appearing already-open on its first paint.
    void el.offsetWidth;
    el.setAttribute("data-open", "");

    var panel = el.querySelector(".pop__panel");
    var focusables = panel.querySelectorAll("a[href], button:not([disabled]), input, [tabindex]:not([tabindex='-1'])");
    if (focusables.length) focusables[0].focus();

    el.addEventListener("click", function (e) {
      if (e.target.closest("[data-pop-close]") || e.target.classList.contains("pop__scrim")) {
        closeModal(el);
      }
    });

    document.addEventListener("keydown", function onKey(e) {
      // The listener outlives the element by design (it is never explicitly
      // removed on close), so it detaches itself the first time it fires
      // after removal rather than leaking for the rest of the session.
      if (!el.parentNode) { document.removeEventListener("keydown", onKey); return; }
      if (e.key === "Escape") { closeModal(el); return; }
      if (e.key !== "Tab" || !focusables.length) return;
      // Wrap Tab at both ends of the panel.
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function buildModal(variant, innerHtml, label) {
    var el = document.createElement("div");
    el.className = "pop pop--" + variant;
    el.innerHTML =
      '<div class="pop__scrim" data-pop-close></div>' +
      '<div class="pop__panel" role="dialog" aria-modal="true" aria-label="' + escapeHtml(label) + '">' +
        '<button class="pop__close" type="button" data-pop-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        innerHtml +
      '</div>';
    return el;
  }

  /* Both popups share a body: title, copy, primary CTA, dismiss.
     The entry offer omits the eyebrow so it states the offer once. Fields the
     merchant left blank are omitted entirely rather than rendered empty, so a
     cleared eyebrow collapses the spacing instead of leaving a gap. */
  function panelHtml(group, opts) {
    var eyebrow = (opts && opts.eyebrow) || conf(group, "eyebrow");
    var title = conf(group, "title");
    var copy = conf(group, "copy");
    var ctaLabel = conf(group, "ctaLabel");
    var ctaHref = conf(group, "ctaHref");
    var skipLabel = conf(group, "skipLabel");

    var html = "";
    if (group !== "entry" && eyebrow) html += '<p class="pop__eyebrow">' + escapeHtml(eyebrow) + "</p>";
    if (title) html += '<h2 class="pop__title">' + escapeHtml(title) + "</h2>";
    if (copy) html += '<p class="pop__copy">' + escapeHtml(copy) + "</p>";

    // A CTA with no destination is a dead button, so it needs both halves.
    var ctas = "";
    if (ctaLabel && ctaHref) {
      ctas += '<a class="pop__cta" href="' + escapeHtml(ctaHref) + '">' + escapeHtml(ctaLabel) + "</a>";
    }
    if (skipLabel) {
      ctas += '<button class="pop__skip" type="button" data-pop-close>' + escapeHtml(skipLabel) + "</button>";
    }
    if (ctas) html += '<div class="pop__ctas">' + ctas + "</div>";

    return html;
  }

  // A popup with nothing but a close button is not worth interrupting for.
  function hasSubstance(group) {
    return !!(conf(group, "title") || conf(group, "copy"));
  }

  // ---- 1. entry popup ---------------------------------------------------
  // First-time visitors. Copy is entirely the merchant's — this build quotes
  // no prices of its own, because the only number it could invent is one the
  // checkout might not honour. If the offer changes, the theme editor field
  // changes with it (see the `entry_copy` info text in the section schema).
  function showEntry() {
    if (!enabled("entry") || !hasSubstance("entry")) return;
    if (read(SEEN_ENTRY_KEY)) return;
    write(SEEN_ENTRY_KEY, "1");
    openModal(buildModal(
      "entry",
      panelHtml("entry"),
      conf("entry", "title") || "Special offer"
    ));
  }

  /* ---- 2. returning-customer prompt ------------------------------------
     Shown to a logged-in customer whose order count Liquid already found to
     be 2 or more. We do not re-check that here — cfg.show carrying "loyalty"
     IS the check, already made against Shopify's own record.

     The order number leads the eyebrow when we have it, because "Order #3" is
     the proof that earns the ask. That is the one piece of copy assembled in
     JS rather than typed in the theme editor, and it is assembled from a
     server-supplied integer, not from anything this browser remembers. */
  function showLoyalty() {
    if (!enabled("loyalty") || !hasSubstance("loyalty")) return;
    if (read(SEEN_LOYALTY_KEY)) return; // ask once, not after every order

    var count = parseInt(cfg.orderCount, 10);
    var eyebrow = conf("loyalty", "eyebrow");
    if (!isNaN(count) && count > 1 && eyebrow) eyebrow = "Order #" + count + " — " + eyebrow;

    write(SEEN_LOYALTY_KEY, "1");
    openModal(buildModal(
      "loyalty",
      panelHtml("loyalty", { eyebrow: eyebrow }),
      conf("loyalty", "title") || "Create your account"
    ));
  }

  /* ---- entry point -----------------------------------------------------
     No page-type test in here. layout/theme.liquid already wraps the section
     in `{% unless request.page_type == 'cart' %}`, so on the cart page this
     file is never even requested — one gate, in the layer that knows the page
     type for certain, instead of two that can disagree. */
  function init() {
    var wait = delayMs();
    if (cfg.show === "loyalty") {
      setTimeout(showLoyalty, wait);
    } else if (cfg.show === "entry") {
      setTimeout(showEntry, wait);
    }
    // Any other value of `show` means Liquid decided nobody should be
    // interrupted. Do nothing — silence is a valid decision, not a bug.
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
